import { useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type RangeOption = {
  id: string;
  label: string;
};

export function RangePicker({
  id,
  value,
  onChange,
  options,
  rangeLabel,
  parseCustomRange,
  customRangePrefix,
}: {
  id?: string;
  value: string;
  onChange: (id: string) => void;
  options: RangeOption[];
  rangeLabel: (rangeId: string) => string;
  parseCustomRange: (rangeId: string) => { from: number; to: number } | null;
  customRangePrefix: string;
}) {
  const current = rangeLabel(value);
  const existingCustom = parseCustomRange(value);
  const [showCustom, setShowCustom] = useState(Boolean(existingCustom));
  const [customStart, setCustomStart] = useState(
    existingCustom
      ? new Date(existingCustom.from).toISOString().slice(0, 10)
      : "",
  );
  const [customEnd, setCustomEnd] = useState(
    existingCustom
      ? new Date(existingCustom.to).toISOString().slice(0, 10)
      : "",
  );

  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    const next = parseCustomRange(value);
    if (next) {
      setCustomStart(new Date(next.from).toISOString().slice(0, 10));
      setCustomEnd(new Date(next.to).toISOString().slice(0, 10));
    }
  }

  function applyCustomRange() {
    if (!customStart || !customEnd) return;
    if (new Date(customStart).getTime() > new Date(customEnd).getTime()) return;
    onChange(`${customRangePrefix}${customStart}|${customEnd}`);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger id={id} className="flex h-10 items-center justify-between gap-2 rounded-xl border border-line bg-surface px-3 text-sm text-fg hover:bg-surface-2">
        <span className="flex items-center gap-2">
          <Globe size={14} className="text-fg-mute" />
          {current}
        </span>
        <ChevronDown size={14} className="text-fg-mute" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {options.map((rangeOption) => (
          <DropdownMenuItem
            key={rangeOption.id}
            onSelect={() => onChange(rangeOption.id)}
          >
            {rangeOption.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            setShowCustom(true);
          }}
        >
          Custom range...
        </DropdownMenuItem>
        {showCustom ? (
          <div className="flex min-w-[260px] flex-col gap-2 px-2 py-2">
            <div className="grid grid-cols-1 gap-2">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-8"
              />
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-8"
              />
            </div>
            <Button
              size="sm"
              onClick={applyCustomRange}
              disabled={!customStart || !customEnd}
            >
              Apply custom range
            </Button>
          </div>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
